import { History } from 'history'
import qs from 'qs'

import {
  InvoiceRecipientType,
  AdministratorType,
  InvoiceRecipientAdminApiType,
  PersonAdminInfoType,
} from 'admin/types'
import {
  AdminApiInterface,
  TransactionsParamsType,
  InvoicesParamsType,
} from 'admin/types/api'
import Api from 'services/api'
import { ConfigType, AdminTypes } from 'types'
import { AuthenticatorInterface } from 'types/authenticator'

export const invoiceParamsToQueryString = (
  params: InvoicesParamsType,
): string => {
  const { filters, ...otherParams } = params
  const activeFilters = {}
  for (const filter in filters) {
    if (filters[filter]) {
      activeFilters[filter] = filters[filter]
    }
  }
  return qs.stringify({ ...otherParams, ...activeFilters }, { indices: false })
}

export default class AdminApi extends Api implements AdminApiInterface {
  constructor(
    config: ConfigType,
    history: History,
    authenticator: AuthenticatorInterface,
  ) {
    super(config, history, authenticator)
  }

  getOrgAccount(orgAccountId) {
    return this.request({
      method: 'get',
      url: `/fim/rest/org-accounts/v1/${orgAccountId}`,
      transformResponse: data => data && data.orgAccount,
    })
  }

  getOrgAccountsByOrgKimCustomerId(orgKimId, status) {
    return this.request({
      method: 'get',
      url: `/fim/rest/org-accounts/v1/list/org-kim-id/${orgKimId}`,
      transformResponse: ({ orgAccounts }) => {
        // Filter by status
        if (status && orgAccounts) {
          const statuses = Array.isArray(status) ? status : [status]
          orgAccounts = orgAccounts.filter(orgAccount =>
            statuses.includes(orgAccount.orgAccountStatus),
          )
        }
        return this.sortOrgAccounts(orgAccounts)
      },
    })
  }

  
  getOrganization(orgKimId) {
    return this.request({
      method: 'get',
      url: `fim/rest/organizations/get/v1/${orgKimId}`,
      transformResponse: (data) => {
        return data 
      },
    })
  }

  createOrgAccount(data) {
    return this.request(
      {
        method: 'post',
        url: '/fia/rest/invoice-purchase/org-accounts/create/v1',
        transformResponse: response => response && response.applicationId,
      },
      data,
    )
  }

  updateOrgAccount(orgAccountId, data) {
    return this.request(
      {
        method: 'put',
        url: `/fim/rest/org-accounts/v1/${orgAccountId}`,
      },
      data,
    )
  }

  inactivateOrgAccount(orgAccountId) {
    return this.request({
      method: 'post',
      url: `/fim/rest/org-accounts/inactivate/v1/${orgAccountId}`,
    })
  }

  updateOrganization(orgKimId, foodCompany) {
    return this.request(
      {
        method: 'put',
        url: `/fim/rest/organizations/v1/${orgKimId}/update`
      },
      { foodCompany },
    )
  }

  createAdministrator(orgAccountId, person, adminType) {
    return this.request(
      {
        method: 'post',
        url: '/fia/rest/administrator/v1',
        transformResponse: response => response && response.administratorId,
      },
      { orgAccountId, person, adminType },
    )
  }

  inactivateAccountAdministrator(orgAccountId, kimId) {
    return this.request(
      {
        method: 'post',
        url: `/fim/rest/roles/administrators/org-account/inactivate/v1`,
      },
      { orgAccountId, kimId },
    )
  }

  inactivateOrganizationAdministrator(orgNumber, kimId) {
    return this.request(
      {
        method: 'post',
        url: `/fim/rest/roles/administrators/organization/inactivate/v1`,
      },
      { orgNumber, kimId },
    )
  }

  getAdministratorsExcel(orgKimId: number) {
    return this.request(
      {
        method: 'get',
        url: `/fim/rest/roles/administrators/excel/v1/${orgKimId}`,
        responseType: 'blob',
      },
      null,
      true,
    )
  }

  createSignatory(data) {
    return this.request(
      {
        method: 'post',
        url: '/fim/rest/roles/signatories/v1',
        transformResponse: response => response && response.signatoryId,
      },
      data,
    )
  }

  getPersonInfo(orgAccountId, kimId, status) {
    return this.request({
      method: 'get',
      url: `/fim/rest/roles/person/v1/${orgAccountId}/${kimId}`,
      transformResponse: data => {
        // TODO: Remove status filtering when handled by API
        if (status && data && data.paymentPurchasers) {
          const statuses = Array.isArray(status) ? status : [status]
          data.paymentPurchasers = data.paymentPurchasers.filter(purchaser =>
            statuses.includes(purchaser.status),
          )
        }
        return data
      },
    })
  }

  updatePersonInfo(orgAccountId, kimId, data) {
    return this.request(
      {
        method: 'put',
        url: `/fim/rest/roles/person/v1/${orgAccountId}/${kimId}`,
      },
      data,
    )
  }

  /**
   * /fim/rest/roles/member/v1/{kimId} only returns active accounts and therefor also
   * this function
   * @param kimId
   */
  getPersonMemberInfo(kimId) {
    return this.request({
      method: 'get',
      url: `/fim/rest/roles/member/v1/${kimId}`,
      transformResponse: (data: PersonAdminInfoType) => {
        if (data && data.organizations) {
          data.organizations = data.organizations.map(organization => {
            if (organization.orgAccounts) {
              // Sort accounts
              organization.orgAccounts = this.sortOrgAccounts(
                organization.orgAccounts,
              )
            }
            return organization
          })
        }
        return data
      },
    })
  }

  createPaymentPurchaser(person, invoiceRecipientId, purchaseLimit) {
    const data = { person, invoiceRecipientId, purchaseLimit }
    return this.request(
      {
        method: 'post',
        url: `/fia/rest/purchaser/v1`,
        transformResponse: response => response && response.paymentPurchaserId,
      },
      data,
    )
  }

  updatePurchaser(purchaserId, purchaseLimit, cardToken) {
    const data = { purchaseLimit, cardToken }
    return this.request(
      {
        method: 'put',
        url: `/fim/rest/purchaser/v1/${purchaserId}`,
      },
      data,
    )
  }

  inactivatePurchaser(purchaserId) {
    return this.request({
      method: 'post',
      url: `/fim/rest/purchaser/inactivate/v1/${purchaserId}`,
    })
  }

  getPaymentPurchaser(urlSafeCardToken) {
    return this.request({
      method: 'get',
      url: `/fim/rest/purchaser/v1/${urlSafeCardToken}`,
    })
  }

  getPaymentPurchaserForId(purchaserId) {
    return this.request({
      method: 'get',
      url: `/fim/rest/purchasers/v1/${purchaserId}`,
    })
  }

  getPaymentPurchasersList(orgAccountId, status) {
    return this.request({
      method: 'get',
      // TODO: Use API call status filtering when implemented
      // url: `/fim/rest/purchasers/org-account/v1/${orgAccountId}?status=${status}`,
      url: `/fim/rest/purchasers/org-account/v1/${orgAccountId}`,
      transformResponse: data => {
        if (data && data.purchasers) {
          if (status) {
            const statuses = Array.isArray(status) ? status : [status]
            return data.purchasers.filter(purchaser =>
              statuses.includes(purchaser.status),
            )
          } 
            return data.purchasers
          
        } 
          return null
        
      },
    })
  }

  getPaymentPurchasersCount(orgAccountId, status) {
    return this.request({
      method: 'get',
      url: `/fim/rest/purchasers/count/v1/${orgAccountId}?status=${status}`,
      transformResponse: data => data && data.numPurchasers,
    })
  }

  getPaymentPurchasersExcel(orgKimId: number) {
    return this.request(
      {
        method: 'get',
        url: `/fim/rest/purchasers/excel/v1/${orgKimId}`,
        responseType: 'blob',
      },
      null,
      true,
    )
  }

  getActivePurchasersCount(orgAccountId) {
    return this.request({
      method: 'get',
      url: `/fim/rest/roles/person/active/purchasers/count/v1/${orgAccountId}`,
      transformResponse: data => data && data.numPurchasers,
    })
  }

  getInvoiceRecipients(orgAccountId) {
    return this.request({
      method: 'get',
      url: `/fim/rest/invoice-recipients/org_account/v1/${orgAccountId}`,
      transformResponse: (data: {
        [invoiceRecipients: string]: InvoiceRecipientType[]
      }) => {
        if (data && data.invoiceRecipients) {
          return data.invoiceRecipients
        } 
          return null
        
      },
    })
  }

  setInvoiceRecipient(data) {
    return this.request(
      {
        method: 'put',
        url:
          `/fim/rest/invoice-recipients/v1${ 
          data.invoiceRecipientId
            ? `?invoiceRecipientId=${data.invoiceRecipientId}`
            : ''}`,
        transformResponse: response => response && response.invoiceRecipientId,
      },
      data,
    )
  }

  inactivateInvoiceRecipient(invoiceRecipientId) {
    return this.request({
      method: 'post',
      url: `/fim/rest/invoice-recipients/inactivate/v1/${invoiceRecipientId}`,
    })
  }

  getInvoiceRecipientAdmins(orgAccountId, invoiceRecipientId) {
    return this.request({
      method: 'get',
      url: `/fim/rest/roles/administrators/invoice-recipient/v1/${invoiceRecipientId}`,
      transformResponse: (data: InvoiceRecipientAdminApiType[]) => {
        if (data && data.length > 0) {
          const res: AdministratorType[] = data.map(admin => {
            return {
              ...admin,
              administratorType: AdminTypes.INVOICE_RECIPIENT_ADMIN,
            }
          })
          return res
        }
        return data
      },
    })
  }

  createInvoiceRecipientAdmin(orgAccountId, invoiceRecipientId, person) {
    const data = {
      orgAccountId,
      invoiceRecipientId,
      person,
      adminType: AdminTypes.INVOICE_RECIPIENT_ADMIN,
    }
    return this.request(
      {
        method: 'post',
        url: `/fia/rest/administrator/v1`,
      },
      data,
    )
  }

  inactivateInvoiceRecipientAdmin(invoiceRecipientId, kimId) {
    const data = { invoiceRecipientId, kimId }
    return this.request(
      {
        method: 'post',
        url: `/fim/rest/roles/administrators/invoice-recipient/inactivate/v1`,
      },
      data,
    )
  }

  getTransactions(orgAccountId, params?: TransactionsParamsType) {
    let paramString = ''
    if (params) {
      const { filters, ...otherParams } = params
      const activeFilters = {}
      for (const filter in filters) {
        if (filters[filter]) {
          activeFilters[filter] = filters[filter]
        }
      }
      paramString = qs.stringify({ ...otherParams, ...activeFilters })
    }
    return this.request({
      method: 'get',
      url: `/ftm/rest/transactions/org-account/v1/${orgAccountId}?${paramString}`,
    })
  }

  getTransaction(transactionId) {
    return this.request({
      method: 'get',
      url: `/ftm/rest/transactions/v1/${transactionId}`,
      transformResponse: response => response && response.transaction,
    })
  }

  getInvoices(orgAccountId, params?: InvoicesParamsType) {
    let paramString = ''
    if (params) {
      paramString = invoiceParamsToQueryString(params)
    }
    return this.request({
      method: 'get',
      url: `/fakir/rest/invoice-specification/org-account/${orgAccountId}/v1?${paramString}`,
      transformResponse: response => response && response.invoiceSpecifications,
    })
  }

  getInvoice(invoiceId) {
    return this.request({
      method: 'get',
      url: `fakir/rest/invoice-specification/v1/${invoiceId}`,
    })
  }

  getInvoicePDF(invoiceId) {
    return this.request(
      {
        method: 'get',
        url: `fakir/rest/invoice-specification/pdf/v1/${invoiceId}`,
        responseType: 'blob',
      },
      null,
      true,
    )
  }

  getInvoiceSpecificationExcel(invoiceId) {
    return this.request(
      {
        method: 'get',
        url: `fakir/rest/invoice-specification/excel/v1/${invoiceId}`,
        responseType: 'blob',
      },
      null,
      true,
    )
  }

  // Utility methods

  sortOrgAccounts = orgAccounts => {
    return orgAccounts.sort((a, b) => {
      if (a.orgAccountName < b.orgAccountName) return -1
      if (a.orgAccountName > b.orgAccountName) return 1
      return 0
    })
  }
}
